import React, { useEffect, useState } from "react";
import client, { apiError, API_BASE } from "@/lib/api";
import { Integration, SyncJob } from "@/lib/types";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, StatusBadge, EmptyState } from "@/components/shared";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, Plug, Loader2, History, ShoppingBag, Wallet } from "lucide-react";

const providerIcons: Record<string, React.ElementType> = {
  shopify: ShoppingBag,
  amazon: ShoppingBag,
  mercadolibre: ShoppingBag,
  mercadopago: Wallet,
};

const IntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [jobs, setJobs] = useState<SyncJob[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [connectOpen, setConnectOpen] = useState<Integration | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [form, setForm] = useState({ api_key: "", api_secret: "", store_url: "", account_id: "" });
  const [lastJobs, setLastJobs] = useState<Record<string, SyncJob | null>>({});
  const [mlForm, setMlForm] = useState({ client_id: "", client_secret: "" });
  const [mlBusy, setMlBusy] = useState(false);
  const [amzForm, setAmzForm] = useState({ client_id: "", client_secret: "", refresh_token: "", seller_id: "" });
  const [amzBusy, setAmzBusy] = useState(false);

  const ML_REDIRECT = `${API_BASE}/integrations/mercadolibre/oauth/callback`;

  const connectAmazon = async () => {
    if (!amzForm.client_id || !amzForm.client_secret || !amzForm.refresh_token || !amzForm.seller_id) {
      toast.error("Completa los 4 campos de Amazon");
      return;
    }
    setAmzBusy(true);
    try {
      await client.post("/integrations/amazon/credentials", {
        client_id: amzForm.client_id.trim(),
        client_secret: amzForm.client_secret.trim(),
        refresh_token: amzForm.refresh_token.trim(),
        seller_id: amzForm.seller_id.trim(),
      });
      toast.success("Amazon conectado. Sincronizando pedidos…");
      setConnectOpen(null);
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAmzBusy(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ml = params.get("ml");
    if (ml === "connected") {
      if (params.get("norefresh") === "1") {
        toast.warning("Mercado Libre conectado, pero tu app NO entregó refresh token. Habilita 'offline_access' en tu aplicación de Mercado Libre y vuelve a conectar, o la conexión expirará en ~6 horas.", { duration: 12000 });
      } else {
        toast.success("Mercado Libre conectado correctamente");
      }
      window.history.replaceState({}, "", "/integrations");
    } else if (ml === "error") {
      toast.error("No se pudo autorizar Mercado Libre. Revisa las credenciales y la Redirect URI e inténtalo de nuevo.");
      window.history.replaceState({}, "", "/integrations");
    }
  }, []);

  const connectMercadoLibre = async () => {
    if (!mlForm.client_id || !mlForm.client_secret) {
      toast.error("Ingresa App ID y Secret Key");
      return;
    }
    setMlBusy(true);
    try {
      await client.post("/integrations/mercadolibre/credentials", {
        client_id: mlForm.client_id.trim(),
        client_secret: mlForm.client_secret.trim(),
        redirect_uri: ML_REDIRECT,
      });
      const res = await client.get("/integrations/mercadolibre/oauth/start");
      window.location.assign(res.data.url);
    } catch (err) {
      toast.error(apiError(err));
      setMlBusy(false);
    }
  };

  const load = async () => {
    const res = await client.get("/integrations");
    setIntegrations(res.data);
    const connected = res.data.filter((i: Integration) => i.status === "connected");
    const entries = await Promise.all(
      connected.map(async (i: Integration) => {
        const j = await client.get(`/integrations/${i.provider}/sync-jobs`);
        return [i.provider, j.data[0] || null] as [string, SyncJob | null];
      })
    );
    setLastJobs(Object.fromEntries(entries));
  };

  const loadJobs = async (provider: string) => {
    setSelectedProvider(provider);
    const res = await client.get(`/integrations/${provider}/sync-jobs`);
    setJobs(res.data);
  };

  useEffect(() => {
    load();
  }, []);

  const connect = async () => {
    if (!connectOpen) return;
    try {
      await client.post(`/integrations/${connectOpen.provider}/connect`, form);
      toast.success(`${connectOpen.label} conectado`);
      setConnectOpen(null);
      setForm({ api_key: "", api_secret: "", store_url: "", account_id: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const sync = async (provider: string) => {
    setSyncing(provider);
    try {
      const res = await client.post(`/integrations/${provider}/sync`);
      if (res.data.status === "running") {
        toast.success("Sincronización iniciada… se actualizará al terminar.");
        // Polling: el sync corre en segundo plano (Amazon puede tardar por rate limits)
        const jobId = res.data.id;
        const poll = setInterval(async () => {
          try {
            const jobs = await client.get(`/integrations/${provider}/sync-jobs`);
            const j = (jobs.data || []).find((x: any) => x.id === jobId);
            if (j && j.status !== "running") {
              clearInterval(poll);
              setSyncing(null);
              if (j.status === "success") {
                toast.success(`Sync completado · ${j.orders_synced} órdenes, ${j.movements_synced} movimientos`);
              } else {
                toast.error(`Sync con error: ${j.error || "revisa la conexión"}`);
              }
              load();
              if (selectedProvider === provider) loadJobs(provider);
            }
          } catch { /* seguir intentando */ }
        }, 4000);
        // Salvaguarda: dejar de esperar tras 4 min
        setTimeout(() => { clearInterval(poll); setSyncing((s) => (s === provider ? null : s)); load(); }, 240000);
        return;
      }
      toast.success(
        `Sync completado · ${res.data.orders_synced} órdenes, ${res.data.movements_synced} movimientos`
      );
      load();
      if (selectedProvider === provider) loadJobs(provider);
      setSyncing(null);
    } catch (err) {
      toast.error(apiError(err));
      setSyncing(null);
    }
  };

  return (
    <div data-testid="integrations-page">
      <PageHeader title="Integraciones" subtitle="Conecta y sincroniza tus canales de venta y cobro" />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {integrations.map((it) => {
          const Icon = providerIcons[it.provider] || Plug;
          const connected = it.status === "connected";
          return (
            <div key={it.provider} className="border border-slate-200 bg-white p-5" data-testid={`integration-${it.provider}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-100">
                    <Icon className="h-5 w-5 text-slate-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">{it.label}</div>
                    <StatusBadge
                      status={it.status}
                      label={connected ? "Conectado" : "Desconectado"}
                      testid={`status-${it.provider}`}
                    />
                  </div>
                </div>
              </div>

              {connected && (
                <div className="mt-4 space-y-1 border-t border-slate-100 pt-4 text-xs text-slate-500">
                  {it.config_masked?.store_url && <div>Tienda: {it.config_masked.store_url}</div>}
                  {it.config_masked?.account_id && <div>Cuenta: {it.config_masked.account_id}</div>}
                  <div>Último sync: {fmtDateTime(it.last_sync_at || undefined)}</div>
                  {lastJobs[it.provider] && (
                    <div className="flex items-center gap-1.5 pt-1" data-testid={`last-sync-${it.provider}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${lastJobs[it.provider]!.status === "success" ? "bg-emerald-500" : "bg-red-500"}`} />
                      {lastJobs[it.provider]!.orders_synced} órdenes · {lastJobs[it.provider]!.movements_synced} mov ·{" "}
                      <span className={`font-medium ${lastJobs[it.provider]!.triggered_by === "sync-automatico" ? "text-[#DD0000]" : "text-slate-600"}`}>
                        {lastJobs[it.provider]!.triggered_by === "sync-automatico" ? "Automático" : "Manual"}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setConnectOpen(it)}
                  data-testid={`connect-${it.provider}`}
                  className="flex items-center gap-2 rounded-none border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <Plug className="h-4 w-4" strokeWidth={1.5} />
                  {connected ? "Reconfigurar" : "Conectar"}
                </button>
                {connected && (
                  <button
                    onClick={() => sync(it.provider)}
                    disabled={syncing === it.provider}
                    data-testid={`sync-${it.provider}`}
                    className="flex items-center gap-2 rounded-none bg-[#DD0000] px-3 py-2 text-sm text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
                  >
                    {syncing === it.provider ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
                    )}
                    Sync manual
                  </button>
                )}
                {connected && (
                  <button
                    onClick={() => loadJobs(it.provider)}
                    data-testid={`history-${it.provider}`}
                    className="flex items-center gap-2 rounded-none border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <History className="h-4 w-4" strokeWidth={1.5} />
                    Historial
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sync history */}
      {selectedProvider && (
        <div className="mt-8 border border-slate-200 bg-white" data-testid="sync-history">
          <div className="border-b border-slate-100 px-5 py-4">
            <h3 className="text-sm font-semibold text-slate-800">Historial de sync · {selectedProvider}</h3>
          </div>
          {jobs.length === 0 ? (
            <EmptyState message="Sin sincronizaciones registradas" />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3">Inicio</th>
                  <th className="px-5 py-3">Fin</th>
                  <th className="px-5 py-3">Estado</th>
                  <th className="px-5 py-3 text-right">Órdenes</th>
                  <th className="px-5 py-3 text-right">Movimientos</th>
                  <th className="px-5 py-3">Ejecutado por</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-5 py-3 text-slate-600">{fmtDateTime(j.started_at)}</td>
                    <td className="px-5 py-3 text-slate-600">{fmtDateTime(j.finished_at)}</td>
                    <td className="px-5 py-3">
                      <StatusBadge status={j.status} label="Éxito" />
                    </td>
                    <td className="px-5 py-3 text-right tabular">{j.orders_synced}</td>
                    <td className="px-5 py-3 text-right tabular">{j.movements_synced}</td>
                    <td className="px-5 py-3 text-slate-500">{j.triggered_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Connect dialog */}
      <Dialog open={!!connectOpen} onOpenChange={(o) => !o && setConnectOpen(null)}>
        <DialogContent className="rounded-none" data-testid="connect-dialog">
          {connectOpen?.provider === "mercadolibre" ? (
            <>
              <DialogHeader>
                <DialogTitle>Conectar Mercado Libre México</DialogTitle>
                <DialogDescription>
                  Ingresa las credenciales de tu app de Mercado Libre. Al continuar te llevaremos a Mercado Libre para autorizar (solo lectura).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">App ID (Client ID)</Label>
                  <Input
                    value={mlForm.client_id}
                    onChange={(e) => setMlForm({ ...mlForm, client_id: e.target.value })}
                    data-testid="ml-client-id"
                    placeholder="Tu App ID (número)"
                    className="mt-1.5 rounded-none border-slate-300"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">Secret Key (Client Secret)</Label>
                  <Input
                    value={mlForm.client_secret}
                    onChange={(e) => setMlForm({ ...mlForm, client_secret: e.target.value })}
                    data-testid="ml-client-secret"
                    type="password"
                    className="mt-1.5 rounded-none border-slate-300"
                  />
                </div>
                <div className="border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500">
                  <div className="mb-1 font-bold uppercase tracking-wider text-slate-600">Redirect URI a registrar en tu app</div>
                  <code className="break-all text-slate-700">{ML_REDIRECT}</code>
                </div>
              </div>
              <DialogFooter>
                <button
                  onClick={connectMercadoLibre}
                  disabled={mlBusy}
                  data-testid="ml-connect-submit"
                  className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
                >
                  {mlBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Guardar y autorizar en Mercado Libre
                </button>
              </DialogFooter>
            </>
          ) : connectOpen?.provider === "amazon" ? (
            <>
              <DialogHeader>
                <DialogTitle>Conectar Amazon Seller Central (México)</DialogTitle>
                <DialogDescription>
                  Pega las credenciales LWA de tu app privada de Amazon (Seller Central → Develop Apps → Authorize). Solo lectura.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">LWA Client ID</Label>
                  <Input value={amzForm.client_id} onChange={(e) => setAmzForm({ ...amzForm, client_id: e.target.value })}
                    data-testid="amz-client-id" placeholder="amzn1.application-oa2-client..." className="mt-1.5 rounded-none border-slate-300" />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">LWA Client Secret</Label>
                  <Input value={amzForm.client_secret} onChange={(e) => setAmzForm({ ...amzForm, client_secret: e.target.value })}
                    data-testid="amz-client-secret" type="password" className="mt-1.5 rounded-none border-slate-300" />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">LWA Refresh Token</Label>
                  <Input value={amzForm.refresh_token} onChange={(e) => setAmzForm({ ...amzForm, refresh_token: e.target.value })}
                    data-testid="amz-refresh-token" type="password" placeholder="Atzr|..." className="mt-1.5 rounded-none border-slate-300" />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">Selling Partner ID (Merchant ID)</Label>
                  <Input value={amzForm.seller_id} onChange={(e) => setAmzForm({ ...amzForm, seller_id: e.target.value })}
                    data-testid="amz-seller-id" placeholder="A2XXXXXXXXXX" className="mt-1.5 rounded-none border-slate-300" />
                </div>
                <div className="border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500">
                  Marketplace México: <code className="text-slate-700">A1AM78C64UM0Y8</code> (ya configurado)
                </div>
              </div>
              <DialogFooter>
                <button onClick={connectAmazon} disabled={amzBusy} data-testid="amz-connect-submit"
                  className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60">
                  {amzBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Conectar Amazon
                </button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Conectar {connectOpen?.label}</DialogTitle>
                <DialogDescription>Ingresa las credenciales del proveedor. En esta demo la conexión es simulada.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">API Key</Label>
                  <Input
                    value={form.api_key}
                    onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                    data-testid="connect-api-key"
                    placeholder="sk_live_..."
                    className="mt-1.5 rounded-none border-slate-300"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">API Secret</Label>
                  <Input
                    value={form.api_secret}
                    onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
                    data-testid="connect-api-secret"
                    type="password"
                    className="mt-1.5 rounded-none border-slate-300"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                    URL / ID de cuenta
                  </Label>
                  <Input
                    value={form.store_url}
                    onChange={(e) => setForm({ ...form, store_url: e.target.value, account_id: e.target.value })}
                    data-testid="connect-store-url"
                    placeholder="mitienda.myshopify.com"
                    className="mt-1.5 rounded-none border-slate-300"
                  />
                </div>
              </div>
              <DialogFooter>
                <button
                  onClick={connect}
                  data-testid="connect-submit"
                  className="rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000]"
                >
                  Conectar
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsPage;
